// The Herald's Hall beacon: the owner's visit and activity view at
// huntermoser.com (contract: the citadel repo's docs/plan-herald.md §1-2).
//
// public/index.html loads https://huntermoser.com/herald.js, which defines
// window.herald and records page views and clicks by itself. This module is
// the game's only door to it. Every call is guarded, so a blocked, slow or
// missing script never touches the game. Calls made before the script has
// loaded wait for it (the identity, and a short queue of events).

const MAX_QUEUED_EVENTS = 20;

let pendingIdentity; // undefined = nothing waiting; [uid|null, name|null] otherwise
const pendingEvents = [];
let waiting = false;

function herald() {
  try {
    return typeof window !== 'undefined' ? window.herald : undefined;
  } catch {
    return undefined;
  }
}

function flush() {
  const h = herald();
  if (!h) return;
  try {
    if (pendingIdentity !== undefined) {
      const [uid, name] = pendingIdentity;
      pendingIdentity = undefined;
      h.identify?.(uid, name);
    }
    while (pendingEvents.length) {
      const [name, props] = pendingEvents.shift();
      h.event?.(name, props);
    }
  } catch {
    /* the beacon is never allowed to break the game */
  }
}

function waitForScript() {
  if (waiting || typeof window === 'undefined') return;
  waiting = true;
  const script = document.getElementById('herald-beacon');
  script?.addEventListener('load', flush, { once: true });
  // Belt and braces for a script that loaded between our check and the listener.
  window.addEventListener('load', flush, { once: true });
}

/** Tag this browser with the signed-in player; `identify(null)` on sign-out. */
export function identify(userId, displayName) {
  const uid = userId == null ? null : String(userId);
  const name = uid == null ? null : (displayName || null);
  const h = herald();
  if (h) {
    try {
      h.identify?.(uid, name);
    } catch {
      /* ignore */
    }
    return;
  }
  pendingIdentity = [uid, name];
  waitForScript();
}

/** A custom event: guess, solve, fail, item_used, purchase, shop_open,
 * item_view, api_error (see PRIVATE_API_README.md, "Beacon events"). */
export function heraldEvent(name, props = {}) {
  const h = herald();
  if (h) {
    try {
      h.event?.(name, props);
    } catch {
      /* ignore */
    }
    return;
  }
  if (pendingEvents.length < MAX_QUEUED_EVENTS) pendingEvents.push([name, props]);
  waitForScript();
}

// --- api_error -------------------------------------------------------------

const API_ERROR_DEDUPE_MS = 60_000;
const recentApiErrors = new Map(); // "METHOD path status" -> last sent (ms)

/** `/api/campaign/39/screen?x=1` -> `/api/campaign/:id/screen`. */
export function normalizeApiPath(path) {
  try {
    const bare = String(path || '').split('?')[0].split('#')[0];
    return bare
      .split('/')
      .map((part) => (/^\d+$/.test(part) || /^[0-9a-f-]{16,}$/i.test(part) ? ':id' : part))
      .join('/');
  } catch {
    return null;
  }
}

/** An API call the player hit that failed: HTTP status, or 0 for a network
 * failure. The same method + path + status is sent at most once a minute. */
export function reportApiError(method, path, status) {
  try {
    const cleanPath = normalizeApiPath(path);
    const verb = String(method || 'GET').toUpperCase();
    const key = `${verb} ${cleanPath} ${status}`;
    const now = Date.now();
    const last = recentApiErrors.get(key);
    if (last && now - last < API_ERROR_DEDUPE_MS) return;
    recentApiErrors.set(key, now);
    if (recentApiErrors.size > 200) recentApiErrors.clear();
    heraldEvent('api_error', { method: verb, path: cleanPath, status: Number(status) || 0 });
  } catch {
    /* ignore */
  }
}
